/**
 * useQuizInsights Hook
 *
 * Fetches live stock data for quiz result insight cards via FMP proxy.
 * Returns loading state and data for rendering insight cards.
 */

import { useState, useEffect, useRef } from 'react';
import type { InsightConfig } from '../quizTypes';
import {
  getQuote,
  getRatios,
  type FMPQuote,
  type FMPRatios,
} from '../../../services/fmpDataService';

/**
 * Data structure for a single insight card.
 */
export interface InsightData {
  config: InsightConfig;
  quote: FMPQuote | null;
  ratios: FMPRatios | null;
}

/**
 * Return type for the useQuizInsights hook.
 */
export interface UseQuizInsightsReturn {
  insights: InsightData[];
  isLoading: boolean;
  hasLiveData: boolean;
}

/**
 * Fetches the appropriate data for an insight config based on its type.
 */
async function fetchInsightData(config: InsightConfig): Promise<InsightData> {
  const { type, symbol } = config;

  // Start with quote (all types need this)
  const quotePromise = getQuote(symbol);

  // Dividend type also needs ratios for yield data
  let ratiosPromise: Promise<FMPRatios[] | null> = Promise.resolve(null);
  if (type === 'dividend') {
    ratiosPromise = getRatios(symbol, 1);
  }

  const [quote, ratiosArray] = await Promise.all([quotePromise, ratiosPromise]);

  // Extract first ratio if available
  const ratios = Array.isArray(ratiosArray) && ratiosArray.length > 0 ? ratiosArray[0] : null;

  return {
    config,
    quote,
    ratios,
  };
}

/**
 * Custom hook that fetches live FMP data for insight cards.
 *
 * @param configs - Array of InsightConfig objects to fetch data for
 * @param enabled - Whether to fetch data (e.g., only when reaching result step)
 * @returns Object with insights array, loading state, and hasLiveData flag
 */
export function useQuizInsights(
  configs: InsightConfig[] | null,
  enabled: boolean
): UseQuizInsightsReturn {
  const [insights, setInsights] = useState<InsightData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Reset mounted ref on mount
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Don't fetch if not enabled or no configs
    if (!enabled || !configs || configs.length === 0) {
      setInsights([]);
      setIsLoading(false);
      return;
    }

    const fetchAllInsights = async () => {
      setIsLoading(true);

      try {
        // Use Promise.allSettled to handle partial failures gracefully
        const results = await Promise.allSettled(configs.map(fetchInsightData));

        if (!mountedRef.current) return;

        // Extract successful results
        const successfulInsights: InsightData[] = [];
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            successfulInsights.push(result.value);
          }
        });

        setInsights(successfulInsights);
      } catch {
        // If something unexpected fails, set empty insights
        if (mountedRef.current) {
          setInsights([]);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchAllInsights();
  }, [configs, enabled]);

  // Check if we have any live data (at least one insight with a quote)
  const hasLiveData = insights.some((insight) => insight.quote !== null);

  return {
    insights,
    isLoading,
    hasLiveData,
  };
}
